import React, { useEffect, useState } from "react";
import api from "../lib/api";
import RanchMapImport from "../components/RanchMapImport";
import OperatorIdImport from "../components/OperatorIdImport";
import { MapPin, Plus, ChevronRight, ChevronDown } from "lucide-react";

const FIELDS = (clients) => [
  { key: "ranch_number", label: "Ranch Number" },
  { key: "name", label: "Ranch Name" },
  { key: "block", label: "Block" },
  { key: "variety", label: "Variety (e.g. Cabernet Sauvignon)" },
  { key: "clone", label: "Clone (e.g. 337)" },
  { key: "rootstock", label: "Rootstock (e.g. 101-14)" },
  { key: "client_id", label: "Client", type: "select", options: [{ value: "", label: "—" }, ...clients.map((c) => ({ value: c.id, label: c.name }))] },
  { key: "address", label: "Street Address" },
  { key: "city", label: "City" },
  { key: "county", label: "County" },
  { key: "state", label: "State" },
  { key: "zip", label: "ZIP Code" },
  { key: "acres", label: "Acres", type: "number" },
  { key: "vines_per_acre", label: "Vines per Acre", type: "number" },
  { key: "farming_type", label: "Farming Program", type: "select", options: [{ value: "Conventional", label: "Conventional" }, { value: "Organic", label: "Organic" }] },
  { key: "apn", label: "APN (Parcel #)" },
  { key: "location", label: "Location Notes (area / directions)" },
  { key: "active", label: "Active", type: "checkbox" },
];

const EMPTY = { ranch_number: "", name: "", block: "", variety: "", clone: "", rootstock: "", client_id: "", address: "", city: "", county: "", state: "CA", zip: "", acres: 0, apn: "", location: "", farming_type: "Conventional", vines_per_acre: 0, active: true };

export default function Ranches() {
  const [items, setItems] = useState([]);
  const [clients, setClients] = useState([]);
  const [editing, setEditing] = useState(null);
  const [expanded, setExpanded] = useState(null);

  const load = async () => { const { data } = await api.get("/ranches?include_inactive=true"); setItems(data); };
  useEffect(() => {
    load();
    api.get("/clients").then(({ data }) => setClients(data)).catch(() => {});
  }, []);

  const save = async () => {
    const body = { ...editing };
    delete body.id; delete body.created_at;
    if (editing.id) await api.put(`/ranches/${editing.id}`, body);
    else await api.post("/ranches", body);
    setEditing(null); load();
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this block?")) return;
    await api.delete(`/ranches/${id}`); load();
  };

  const groups = items.reduce((acc, r) => { (acc[r.ranch_number] = acc[r.ranch_number] || []).push(r); return acc; }, {});
  const clientName = (id) => clients.find((c) => c.id === id)?.name;

  return (
    <div className="space-y-6" data-testid="ranches-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight" style={{ fontFamily: "'Fraunces', serif" }}>Ranches</h1>
          <p className="text-stone-500 text-sm mt-1">Tap a ranch to see its blocks — variety, clone & rootstock</p>
        </div>
        <div className="flex gap-2">
          <OperatorIdImport onDone={load} />
          <RanchMapImport onDone={load} />
          <button data-testid="new-ranches-btn" onClick={() => setEditing({ ...EMPTY })}
            className="h-14 px-6 bg-[#1F3D34] text-white font-bold uppercase border border-stone-200 shadow-sm">+ New</button>
        </div>
      </div>

      <div className="space-y-2">
        {Object.entries(groups).sort(([a], [b]) => a.localeCompare(b, undefined, { numeric: true })).map(([rn, blocks]) => {
          const head = blocks[0];
          const totalAcres = blocks.reduce((s, b) => s + (Number(b.acres) || 0), 0);
          const open = expanded === rn;
          return (
            <div key={rn} className="bg-white border border-stone-200 shadow-sm" data-testid={`ranch-group-${rn}`}>
              <button data-testid={`ranch-toggle-${rn}`} onClick={() => setExpanded(open ? null : rn)}
                className={`w-full text-left px-4 py-3 flex flex-wrap items-center justify-between gap-2 ${open ? "bg-[#1F3D34] text-white" : "hover:bg-stone-50"}`}>
                <div>
                  <div className="font-bold text-lg flex items-center gap-2" style={{ fontFamily: "'Fraunces', serif" }}>
                    {open ? <ChevronDown size={18} /> : <ChevronRight size={18} />} {rn} · {head.name}
                  </div>
                  <div className={`text-xs flex items-center gap-1.5 flex-wrap ml-7 ${open ? "text-white/70" : "text-stone-500"}`}>
                    {(head.address || head.city) && <><MapPin size={11} /> {[head.address, head.city, head.county].filter(Boolean).join(", ")}</>}
                    {clientName(head.client_id) && <span>· {clientName(head.client_id)}</span>}
                  </div>
                </div>
                <div className={`text-xs font-bold uppercase ${open ? "text-white/80" : "text-stone-500"}`}>
                  {blocks.length} block{blocks.length === 1 ? "" : "s"} · {totalAcres.toFixed(1)} ac
                </div>
              </button>
              {open && (
              <>
              <div className="px-4 py-2 border-b border-stone-100 flex justify-end">
                <button data-testid={`add-block-${rn}`}
                  onClick={() => setEditing({ ...EMPTY, ranch_number: rn, name: head.name, address: head.address || "", city: head.city || "", county: head.county || "", state: head.state || "CA", zip: head.zip || "", apn: head.apn || "", client_id: head.client_id || "" })}
                  className="h-9 px-4 bg-[#1F3D34] text-white font-bold uppercase text-xs flex items-center gap-1.5">
                  <Plus size={13} /> Add Block
                </button>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm min-w-[760px]">
                  <thead className="bg-stone-50 border-b border-stone-200">
                    <tr className="text-left text-[10px] uppercase text-stone-500">
                      <th className="p-3">Block</th><th className="p-3">Variety</th><th className="p-3">Clone</th>
                      <th className="p-3">Rootstock</th><th className="p-3">Acres</th><th className="p-3">Vines/Ac</th>
                      <th className="p-3">Program</th><th className="p-3">Active</th><th className="p-3">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {blocks.map((b) => (
                      <tr key={b.id} className={`border-b border-stone-100 ${b.active ? "" : "opacity-40"}`} data-testid={`ranches-row-${b.id}`}>
                        <td className="p-3 font-bold">{b.block || "—"}</td>
                        <td className="p-3">{b.variety || "—"}</td>
                        <td className="p-3">{b.clone || "—"}</td>
                        <td className="p-3">{b.rootstock || "—"}</td>
                        <td className="p-3 font-mono">{b.acres ?? "—"}</td>
                        <td className="p-3 font-mono">{b.vines_per_acre || "—"}</td>
                        <td className="p-3">{b.farming_type || "—"}</td>
                        <td className="p-3">{b.active ? "Yes" : "No"}</td>
                        <td className="p-3 space-x-2 whitespace-nowrap">
                          <button onClick={() => setEditing(b)} className="px-3 h-9 border border-stone-200 font-bold uppercase text-xs hover:bg-[#D8C7A1]">Edit</button>
                          <button onClick={() => remove(b.id)} className="px-3 h-9 border border-stone-200 font-bold uppercase text-xs hover:bg-red-200">Del</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              </>
              )}
            </div>
          );
        })}
        {items.length === 0 && (
          <div className="bg-white border border-stone-200 p-10 text-center text-stone-400 font-bold uppercase text-sm">No ranches yet — import a map or add one</div>
        )}
      </div>

      {editing && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
          <div className="bg-white border border-stone-200 shadow-2xl w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto">
            <h3 className="text-2xl font-black uppercase mb-4" style={{ fontFamily: "'Fraunces', serif" }}>{editing.id ? "Edit" : "New"} Block</h3>
            <div className="space-y-3">
              {FIELDS(clients).map((f) => (
                <div key={f.key}>
                  <label className="text-sm font-bold uppercase tracking-[0.1em] block mb-1">{f.label}</label>
                  {f.type === "select" ? (
                    <select value={editing[f.key] ?? ""} onChange={(e) => setEditing({ ...editing, [f.key]: e.target.value })} className="w-full h-12 px-3 border border-stone-200">
                      {f.options.map((o) => (<option key={o.value} value={o.value}>{o.label}</option>))}
                    </select>
                  ) : f.type === "checkbox" ? (
                    <input type="checkbox" checked={!!editing[f.key]} onChange={(e) => setEditing({ ...editing, [f.key]: e.target.checked })} className="w-5 h-5" />
                  ) : (
                    <input data-testid={`ranches-${f.key}-input`} type={f.type || "text"} value={editing[f.key] ?? ""}
                      onChange={(e) => setEditing({ ...editing, [f.key]: f.type === "number" ? parseFloat(e.target.value) || 0 : e.target.value })}
                      className="w-full h-12 px-3 border border-stone-200" />
                  )}
                </div>
              ))}
              <div className="flex gap-2 pt-2">
                <button data-testid="save-ranches-btn" onClick={save} className="flex-1 h-12 bg-[#1F3D34] text-white font-bold uppercase border border-stone-200">Save</button>
                <button onClick={() => setEditing(null)} className="flex-1 h-12 bg-white font-bold uppercase border border-stone-200">Cancel</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
